import * as React from 'react';
import { UserProvider as Auth0UserProvider } from '@auth0/nextjs-auth0/client';
import type { EmotionCache } from '@emotion/react';
import { CacheProvider } from '@emotion/react';
import { ApiSwrProvider } from '@seccl/api-swr';

import type { AppPageProps } from '../api/serverSession/session.types';
import { isAuthenticatedPageProps } from '../api/serverSession/session.types';
import { Head } from '../components/layout/head';
import { BrandingProvider } from '../context/branding.context';
import { CookieProvider } from '../context/cookie.context';
import { HostProvider } from '../context/host.context';
import { ToolsProvider } from '../context/tools.context';
import { SecclProvider } from './secclProvider';

interface IProps {
  pageProps: AppPageProps;
  emotionCache: EmotionCache;
}

/**
 * Providers needed by every page, whether or not the user is authenticated.
 * Firm and node come from the host, so they are available even when the user is logged out.
 */
export const AppProviders: React.FC<React.PropsWithChildren<IProps>> = ({ pageProps, emotionCache, children }) => {
  const isAuthenticated = isAuthenticatedPageProps(pageProps);
  const { branding, host } = pageProps;

  return (
    <Auth0UserProvider user={isAuthenticated ? pageProps.session.user : undefined}>
      <CookieProvider>
        <ApiSwrProvider value={{ revalidateOnMount: false, revalidateOnFocus: false }}>
          <ToolsProvider showDevTools={isAuthenticated ? pageProps.showDevTools : false}>
            <HostProvider firmId={host?.firmId} nodeId={host?.nodeId}>
              <BrandingProvider
                nodeId={branding?.logoNodeId}
                footerCopyright={branding?.footerCopyright ?? undefined}
                orderExecutionPolicyUrl={branding?.orderExecutionPolicyUrl ?? undefined}
                windowTitle={branding?.windowTitle ?? 'SECCL'}
                logoBaseUrl={branding?.logoBaseUrl ?? undefined}
              >
                <CacheProvider value={emotionCache}>
                  <SecclProvider>
                    {/* Only provide the nodeId once authenticated to stop premature clientside branding requests */}
                    <Head nodeId={isAuthenticated ? host?.nodeId : undefined} branding={branding ?? undefined} />
                    {children}
                  </SecclProvider>
                </CacheProvider>
              </BrandingProvider>
            </HostProvider>
          </ToolsProvider>
        </ApiSwrProvider>
      </CookieProvider>
    </Auth0UserProvider>
  );
};
